import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Box, TextField, Slider, MenuItem, Checkbox, FormControlLabel, Typography, Link, Stack } from "@mui/material";
import StepLayout from "../components/StepLayout";
import ChoiceCard from "../components/ChoiceCard";
import SupportNotice from "../components/SupportNotice";
import { TOPIC_KEYS, SUBTOPIC_KEYS, REGION_KEYS, LANGUAGE_KEYS, GENDER_KEYS, eur } from "../constants/funnel";
import { detectCrisis } from "../util/crisisDetect";
import MatchApi from "../api/MatchApi";
import MutualiteitApi, { Mutualiteit } from "../api/MutualiteitApi";

const REFERENCE_RATE_CENTS = 3750; // indicative coaching price for the preview
const STEPS = ["topic", "subtopic", "note", "wellbeing", "about", "region", "fund", "consent"] as const;

export default function Funnel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [topic, setTopic] = useState("");
  const [subtopic, setSubtopic] = useState("");
  const [note, setNote] = useState("");
  const [wellbeing, setWellbeing] = useState(5);
  const [age, setAge] = useState("");
  const [language, setLanguage] = useState("nl");
  const [genderPref, setGenderPref] = useState("");
  const [region, setRegion] = useState("");
  const [fundCode, setFundCode] = useState("");
  const [terms, setTerms] = useState(false);
  const [medical, setMedical] = useState(false);

  const [funds, setFunds] = useState<Mutualiteit[]>([]);
  useEffect(() => {
    MutualiteitApi.list().then(setFunds).catch(() => setFunds([]));
  }, []);

  const step = STEPS[stepIdx];
  const total = STEPS.length;
  const ageNum = Number(age);
  const lowWellbeing = wellbeing <= 3;
  const crisisInNote = useMemo(() => detectCrisis(note), [note]);

  const subtopicKeys = useMemo(() => SUBTOPIC_KEYS[topic] || [], [topic]);
  const selectedFund = funds.find((f) => f.code === fundCode);
  const youPay = selectedFund
    ? Math.max(0, REFERENCE_RATE_CENTS - Math.min(selectedFund.perSessionCents, REFERENCE_RATE_CENTS))
    : null;

  const back = stepIdx > 0 ? () => setStepIdx((i) => i - 1) : () => navigate("/");
  const advance = () => setStepIdx((i) => Math.min(i + 1, total - 1));

  async function submit() {
    setSubmitting(true);
    try {
      const { matchProfileId } = await MatchApi.create({
        age: ageNum, topic, subtopic: subtopic || undefined, region,
        freeText: note || undefined, fundCode: fundCode || undefined,
        wellbeingScore: wellbeing, consent: { terms, medicalData: medical, version: "v1" },
        referralSource: (() => { try { return sessionStorage.getItem("carefunded_ref") || undefined; } catch { return undefined; } })(),
      });
      navigate(`/resultaat/${matchProfileId}`, { state: { language } });
    } catch {
      setSubmitting(false);
    }
  }

  const canNext: Record<string, boolean> = {
    topic: !!topic, subtopic: !!subtopic, note: true, wellbeing: true,
    about: Number.isFinite(ageNum) && ageNum >= 16,
    region: !!region, fund: !!fundCode, consent: terms && medical,
  };

  const title = t(`funnel.${step}Title`);
  const subtitle = t(`funnel.${step}Subtitle`);

  return (
    <StepLayout
      step={stepIdx + 1}
      total={total}
      title={title}
      subtitle={subtitle}
      onBack={back}
      onNext={step === "consent" ? submit : advance}
      nextLabel={step === "consent" ? (submitting ? t("funnel.submitting") : t("funnel.showMatch")) : t("common.next")}
      nextDisabled={!canNext[step] || (step === "consent" && submitting)}
      onSkip={step === "note" ? advance : undefined}
    >
      {step === "topic" && (
        <Stack spacing={1.5}>
          {TOPIC_KEYS.map((k) => (
            <ChoiceCard key={k} label={t(`topics.${k}.label`)} hint={t(`topics.${k}.hint`)}
              selected={topic === k} onClick={() => { setTopic(k); setSubtopic(""); }} />
          ))}
        </Stack>
      )}

      {step === "subtopic" && (
        <Stack spacing={1.5}>
          {subtopicKeys.map((k) => (
            <ChoiceCard key={k} label={t(`subtopics.${k}`)} selected={subtopic === k} onClick={() => setSubtopic(k)} />
          ))}
        </Stack>
      )}

      {step === "note" && (
        <Box>
          <TextField multiline minRows={5} fullWidth placeholder={t("funnel.notePlaceholder")}
            value={note} onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setNote(e.target.value)} />
          {crisisInNote && <Box sx={{ mt: 3 }}><SupportNotice /></Box>}
        </Box>
      )}

      {step === "wellbeing" && (
        <Box>
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Typography sx={{ fontFamily: "var(--font-display)", fontSize: "3rem", color: "primary.main", lineHeight: 1 }}>
              {wellbeing}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {wellbeing <= 3 ? t("funnel.wellbeingLow") : wellbeing <= 6 ? t("funnel.wellbeingMid") : t("funnel.wellbeingHigh")}
            </Typography>
          </Box>
          <Slider value={wellbeing} onChange={(_: Event, v: number | number[]) => setWellbeing(v as number)} min={1} max={10} step={1} marks
            sx={{ color: "primary.main", "& .MuiSlider-thumb": { width: 24, height: 24 } }} />
          <Box sx={{ display: "flex", justifyContent: "space-between", color: "text.secondary", fontSize: ".85rem" }}>
            <span>{t("funnel.wellbeingMin")}</span><span>{t("funnel.wellbeingMax")}</span>
          </Box>
          {lowWellbeing && <Box sx={{ mt: 3 }}><SupportNotice /></Box>}
        </Box>
      )}

      {step === "about" && (
        <Stack spacing={3}>
          <TextField label={t("funnel.age")} type="number" value={age} onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setAge(e.target.value)}
            helperText={age && ageNum < 16 ? t("funnel.ageError") : t("funnel.ageHelp")}
            error={!!age && ageNum < 16} inputProps={{ min: 16, inputMode: "numeric" }} />
          <TextField select label={t("funnel.languagePref")} value={language} onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setLanguage(e.target.value)}>
            {LANGUAGE_KEYS.map((k) => <MenuItem key={k} value={k}>{t(`language.${k}`)}</MenuItem>)}
          </TextField>
          <TextField select label={t("funnel.genderPref")} value={genderPref} onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setGenderPref(e.target.value)}>
            {GENDER_KEYS.map((k) => <MenuItem key={k || "none"} value={k}>{t(`genderPref.${k || "none"}`)}</MenuItem>)}
          </TextField>
        </Stack>
      )}

      {step === "region" && (
        <Stack spacing={1.5}>
          {REGION_KEYS.map((k) => (
            <ChoiceCard key={k} label={t(`regions.${k}`)} selected={region === k} onClick={() => setRegion(k)} />
          ))}
        </Stack>
      )}

      {step === "fund" && (
        <Box>
          <Stack spacing={1.5}>
            {funds.map((f) => (
              <ChoiceCard key={f.code} label={f.name}
                hint={f.perSessionCents > 0 ? t("funnel.fundBackPerSession", { amount: eur(f.perSessionCents) }) : undefined}
                selected={fundCode === f.code} onClick={() => setFundCode(f.code)} />
            ))}
          </Stack>
          {selectedFund && (
            <Box sx={{ mt: 3, p: 2.5, borderRadius: "var(--radius-lg)", bgcolor: "var(--primary-50)", border: "1px solid var(--primary-100)" }}>
              {youPay !== null && selectedFund.perSessionCents > 0 ? (
                <Typography sx={{ color: "primary.dark", m: 0 }}>
                  {t("funnel.fundPreview", {
                    price: eur(REFERENCE_RATE_CENTS), youPay: eur(youPay),
                    cap: selectedFund.maxSessionsPerYear ? t("funnel.fundPreviewCap", { count: selectedFund.maxSessionsPerYear }) : "",
                  })}
                </Typography>
              ) : (
                <Typography sx={{ color: "text.secondary", m: 0 }}>
                  {t("funnel.fundNone", { price: eur(REFERENCE_RATE_CENTS) })}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      {step === "consent" && (
        <Stack spacing={2}>
          <FormControlLabel
            control={<Checkbox checked={terms} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTerms(e.target.checked)} />}
            label={
              <Trans i18nKey="funnel.consentTerms"
                components={[<Link href="/voorwaarden" target="_blank" rel="noreferrer" />, <Link href="/privacy" target="_blank" rel="noreferrer" />]} />
            }
          />
          <FormControlLabel
            control={<Checkbox checked={medical} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMedical(e.target.checked)} />}
            label={t("funnel.consentMedical")}
          />
          <Typography variant="caption" sx={{ color: "text.secondary" }}>{t("funnel.consentReassure")}</Typography>
        </Stack>
      )}
    </StepLayout>
  );
}
